import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Check,
  ArrowRight,
  Store,
  MapPin,
  MessageCircle,
  ShoppingBag,
} from "lucide-react";
import { Seo, faqSchema } from "@/components/Seo";
import { MaskedLines, Reveal, SectionTag } from "@/components/Motion";
import { ChatButton, FinalCta, openWhatsApp, WA_MESSAGES } from "@/components/Cta";
import FaqAccordion from "@/components/FaqAccordion";
import CoopNote from "@/components/CoopNote";
import { CONSIDERATIONS, OPPORTUNITIES, MERCHANT_TYPES, STAGES, HOME_SERVICES, IMPROVE_AREAS, WHY_POINTS, STEPS, HOME_FAQS } from "@/data/site";

const TRUST_ITEMS = [
  { icon: Check, text: "免费初步咨询" },
  { icon: MapPin, text: "马来西亚市场" },
  { icon: MessageCircle, text: "一对一 WhatsApp 沟通" },
  { icon: Store, text: "有无产品都可先了解" },
  { icon: ShoppingBag, text: "先了解再决定" },
];

const HERO_POINTS = ["免费 WhatsApp 咨询", "免费了解合作方式", "有产品 / 没产品都可了解", "可了解一件代发", "马来西亚市场", "一对一沟通"];

const WHO_CAN_ASK = [
  { title: "想做电商副业", desc: "想了解 TikTok Shop 怎样开始" },
  { title: "没有产品", desc: "想了解一件代发或无需自行囤货方式" },
  { title: "已有 Shopee / Lazada", desc: "想增加 TikTok Shop 渠道" },
  { title: "有实体店", desc: "想拓展线上销售" },
  { title: "已有产品 / 品牌", desc: "想了解 TikTok Shop 是否适合" },
  { title: "完全没经验", desc: "可以先咨询流程和准备事项" },
];

const ASK_FIRST = [
  "TikTok Shop 要怎样开始？",
  "没有产品可以吗？",
  "一件代发是什么方式？",
  "是否需要自己囤货？",
  "已有 Shopee / Lazada 可以怎么做？",
  "实体店适不适合？",
  "后续运营要怎样安排？",
  "需要准备什么？",
];

const PILL_STYLES = {
  done: "bg-lime/30 text-forest",
  active: "bg-forest text-paper",
  planned: "bg-sand text-ink/55",
};

const JOURNEY_STEPS = [
  { name: "Product Assessment", status: "已完成", tone: "done" },
  { name: "Shop Setup", status: "准备中", tone: "planned" },
  { name: "Store Operations", status: "持续支持", tone: "done" },
];

function HeroVisual() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.35, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative"
      data-testid="hero-workspace"
    >
      <div className="overflow-hidden rounded-3xl border border-ink/10 bg-sand">
        <img
          src="/img/hero-consultation-1024.webp"
          srcSet="/img/hero-consultation-640.webp 640w, /img/hero-consultation-1024.webp 1024w, /img/hero-consultation-1600.webp 1600w"
          sizes="(min-width: 1024px) 44vw, 100vw"
          alt="马来西亚商家咨询 TikTok Shop 开店与运营方案"
          loading="eager"
          fetchPriority="high"
          className="aspect-[4/3] w-full object-cover object-[68%_50%] lg:aspect-[5/4]"
        />
      </div>
      <div className="absolute -bottom-4 left-4 hidden rounded-2xl border border-ink/10 bg-white/95 p-4 backdrop-blur-sm sm:block md:-bottom-5 md:left-6">
        <div className="flex items-center gap-2">
          <div className="flex h-6 w-6 items-center justify-center rounded-md bg-forest text-paper">
            <Store size={12} />
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink/50">TKShopMY Seller Journey</p>
        </div>
        <div className="mt-2.5 space-y-1.5">
          {JOURNEY_STEPS.map((t) => (
            <div key={t.name} className="flex items-center justify-between gap-4">
              <span className="text-[11px] font-medium text-ink/80">{t.name}</span>
              <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold ${PILL_STYLES[t.tone]}`}>
                {t.status}
              </span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export default function Home() {
  const schemas = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://tkshopmy.com/#webpage",
      url: "https://tkshopmy.com/",
      name: "TikTok Shop 马来西亚商家入驻与开店支持 | TKShopMY",
      description: "TKShopMY 协助马来西亚商家了解 TikTok Shop、评估产品、准备开店，并提供后续运营支持。",
      isPartOf: { "@id": "https://tkshopmy.com/#website" },
      about: { "@id": "https://tkshopmy.com/#organization" },
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: "TikTok Shop 开店与运营咨询",
      provider: { "@id": "https://tkshopmy.com/#organization" },
      areaServed: { "@type": "Country", name: "Malaysia" },
      name: "TikTok Shop 马来西亚商家入驻与开店支持",
    },
    faqSchema(HOME_FAQS),
  ];

  return (
    <>
      <Seo
        title="TikTok Shop 马来西亚合作咨询 | 免费了解开店与一件代发 | TKShopMY"
        description="TKShopMY 提供马来西亚 TikTok Shop 招商合作咨询：无论有产品、没产品、想做电商副业或了解一件代发，都可以先免费 WhatsApp 咨询，先了解再决定。"
        path="/"
        schemas={schemas}
      />

      {/* HERO */}
      <section className="relative overflow-hidden pt-24 md:pt-28 grain" data-testid="hero-section">
        <div className="pointer-events-none absolute -left-32 top-20 h-96 w-96 rounded-full bg-lime/25 blur-3xl" />
        <div className="mx-auto grid max-w-6xl gap-10 px-5 pb-12 md:px-8 md:pb-16 lg:grid-cols-[1fr_1fr] lg:items-center">
          <div>
            <Reveal y={14}>
              <p className="inline-flex items-center gap-2 rounded-full border border-forest/20 bg-white px-4 py-1.5 text-xs font-semibold tracking-wide text-forest" data-testid="hero-kicker">
                <span className="h-1.5 w-1.5 rounded-full bg-lime" />
                马来西亚 TikTok Shop 合作咨询
              </p>
            </Reveal>
            <MaskedLines
              className="mt-5 font-sans text-3xl font-bold leading-[1.2] tracking-tight text-ink sm:text-4xl lg:text-5xl"
              lines={[<>想做 TikTok Shop？</>]}
            />
            <Reveal delay={0.35}>
              <p className="mt-3 text-lg font-semibold leading-snug text-moss md:text-xl">
                现在开放合作咨询
              </p>
              <p className="mt-2 text-base font-semibold leading-snug text-ink md:text-lg">
                <span className="mr-2 inline-block h-1.5 w-1.5 rounded-full bg-lime ring-4 ring-lime/25 align-middle" />
                有产品、没产品都可以先咨询。先了解，再决定要不要开始。
              </p>
            </Reveal>
            <Reveal delay={0.4}>
              <p className="mt-5 max-w-lg text-sm leading-relaxed text-ink/65 md:text-base" data-testid="hero-subtitle">
                无论你已经有产品、正在做 Shopee / Lazada，还是想做电商副业、了解一件代发，都可以先免费了解适合自己的方式。
              </p>
            </Reveal>
            <Reveal delay={0.55}>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <ChatButton location="hero" variant="primary" testId="hero-chat-button">
                  免费 WhatsApp 了解合作
                </ChatButton>
                <a
                  href="#how-to-start"
                  className="group inline-flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-base font-semibold text-ink transition-colors hover:border-forest hover:text-forest"
                  data-testid="hero-services-button"
                >
                  先看看怎样开始
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </a>
              </div>
              <p className="mt-3 text-[13px] text-ink/50" data-testid="hero-no-form-note">无需填写表格，直接 WhatsApp 咨询</p>
              <div className="mt-6 grid max-w-md grid-cols-2 gap-x-6 gap-y-2" data-testid="hero-trust-points">
                {HERO_POINTS.map((t) => (
                  <span key={t} className="flex items-center gap-1.5 text-[13px] font-medium text-ink/70">
                    <Check size={14} className="text-moss" />
                    {t}
                  </span>
                ))}
              </div>
            </Reveal>
          </div>
          <HeroVisual />
        </div>

        {/* Trust bar */}
        <div className="border-t border-ink/10 bg-white/60">
          <div className="mx-auto max-w-6xl px-5 py-4 md:px-8" data-testid="trust-bar">
            <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 md:flex md:flex-wrap md:items-center md:justify-center md:gap-x-8">
              {TRUST_ITEMS.map((t) => (
                <span key={t.text} className="flex items-center gap-2 text-sm font-medium text-ink/70">
                  <t.icon size={15} className="shrink-0 text-moss" />
                  {t.text}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHO CAN ASK */}
      <section className="bg-white py-14 md:py-20" data-testid="who-can-ask-section">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal>
            <SectionTag>适合谁</SectionTag>
            <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl lg:text-4xl">
              这些情况，都可以先免费了解
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {WHO_CAN_ASK.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-ink/10 bg-paper p-5" data-testid={`who-card-${i}`}>
                  <p className="flex items-center gap-2 text-sm font-bold text-ink">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-lime" />
                    {a.title}
                  </p>
                  <p className="mt-2 text-sm leading-relaxed text-ink/60">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NO PRODUCT + ASK FIRST */}
      <section className="py-14 md:py-20" data-testid="ask-first-section">
        <div className="mx-auto grid max-w-6xl gap-10 px-5 md:px-8 lg:grid-cols-2">
          <Reveal>
            <SectionTag>没有产品也可以</SectionTag>
            <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl">
              暂时没有产品？也可以先了解
            </h2>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-ink/65 md:text-base">
              不是每个人开始 TikTok Shop 时都已经有产品。如果你还没有产品，可以先了解一件代发、无需自行囤货等合作方式，再看看哪一种比较适合你。
            </p>
            <div className="mt-6">
              <ChatButton location="no-product" variant="primary" testId="no-product-chat" message={WA_MESSAGES.dropshipping}>
                免费了解一件代发
              </ChatButton>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <SectionTag>先问清楚</SectionTag>
            <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl">
              合作前，可以先问清楚这些
            </h2>
            <ul className="mt-5 grid gap-x-6 gap-y-2.5 sm:grid-cols-2">
              {ASK_FIRST.map((q) => (
                <li key={q} className="flex items-start gap-2 text-sm text-ink/75">
                  <Check size={15} className="mt-0.5 shrink-0 text-moss" />
                  {q}
                </li>
              ))}
            </ul>
            <div className="mt-6">
              <ChatButton location="ask-first" variant="ghost" testId="ask-first-chat">
                免费 WhatsApp 问一下
              </ChatButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FREE CONSULT BAND */}
      <section className="relative overflow-hidden bg-forest py-10 text-paper md:py-14 grain" data-testid="free-consult-section">
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-lime/15 blur-3xl" />
        <div className="mx-auto max-w-3xl px-5 text-center md:px-8">
          <Reveal>
            <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl">
              先免费了解，再决定是否合作
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-paper/70 md:text-base">
              我们提供免费初步咨询，先了解你的情况、需求和适合的合作方向。咨询本身不收费，是否继续合作，由你了解清楚后再决定。
            </p>
            <div className="mt-6">
              <ChatButton location="free-consult" variant="lime" testId="free-consult-chat">
                免费 WhatsApp 了解合作
              </ChatButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* OPPORTUNITY */}
      <section className="py-14 md:py-20" data-testid="opportunity-section">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal>
            <SectionTag>商家机会</SectionTag>
            <h2 className="max-w-2xl font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl lg:text-4xl">
              已经有产品，为什么还可以考虑 TikTok Shop？
            </h2>
          </Reveal>
          <div className="mt-9 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {OPPORTUNITIES.map((o, i) => (
              <Reveal key={o.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-6" data-testid={`opportunity-card-${i}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-lime block" />
                  <h3 className="mt-3 text-base font-bold leading-snug text-ink">{o.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink/55">{o.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-8">
              <ChatButton location="opportunity" variant="primary" testId="opportunity-chat-button">
                WhatsApp 咨询我的情况
              </ChatButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MERCHANT TYPE SELECTOR */}
      <section className="relative overflow-hidden bg-forest py-14 text-paper md:py-20 grain" data-testid="merchant-section">
        <div className="pointer-events-none absolute -right-24 top-0 h-72 w-72 rounded-full bg-lime/10 blur-3xl" />
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-[0.85fr_1.15fr]">
            <Reveal>
              <div className="overflow-hidden rounded-3xl border border-paper/15">
                <img
                  src="/img/product-assessment-1024.webp"
                  srcSet="/img/product-assessment-640.webp 640w, /img/product-assessment-1024.webp 1024w, /img/product-assessment-1600.webp 1600w"
                  sizes="(min-width: 1024px) 38vw, 100vw"
                  alt="商家与团队评估产品是否适合 TikTok Shop"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover object-[60%_50%]"
                  data-testid="merchant-image"
                />
              </div>
            </Reveal>
            <div>
              <Reveal>
                <SectionTag dark>先看看适不适合</SectionTag>
                <h2 className="max-w-xl font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl lg:text-4xl">
                  你的产品适合 TikTok Shop 吗？
                </h2>
                <p className="mt-4 max-w-lg text-sm leading-relaxed text-paper/65 md:text-base">
                  不同产品适合的经营方式不同。先了解产品、价格、目标客户和目前销售渠道，再决定怎样开始会比较实际。
                </p>
              </Reveal>
              <div className="mt-8 grid gap-3 sm:grid-cols-2">
                {MERCHANT_TYPES.map((m, i) => (
                  <Reveal key={m.key} delay={i * 0.06}>
                    <div className="flex h-full flex-col rounded-2xl border border-paper/15 bg-paper/5 p-5 transition-colors duration-300 hover:border-lime/60 hover:bg-paper/10" data-testid={`merchant-card-${m.key}`}>
                      <h3 className="text-base font-bold leading-snug">{m.title}</h3>
                      <p className="mt-1.5 flex-1 text-sm leading-relaxed text-paper/60">{m.desc}</p>
                      <button
                        onClick={() => openWhatsApp(`merchant-${m.key}`, { text: m.cta, message: WA_MESSAGES.merchant })}
                        className="mt-4 w-full rounded-full border border-lime/50 py-2 text-sm font-semibold text-lime transition-colors hover:bg-lime hover:text-ink"
                        data-testid={`merchant-cta-${m.key}`}
                        data-location={`merchant-${m.key}`}
                      >
                        {m.cta}
                      </button>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONSIDERATIONS */}
      <section className="py-14 md:py-20" data-testid="problems-section">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal>
            <SectionTag>先想清楚</SectionTag>
            <h2 className="max-w-2xl font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl lg:text-4xl">
              你可能正在考虑这些问题
            </h2>
          </Reveal>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {CONSIDERATIONS.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-ink/10 bg-white p-5 transition-colors duration-300 hover:border-forest/40" data-testid={`problem-card-${i}`}>
                  <span className="font-display text-sm italic text-moss">{p.num}</span>
                  <p className="mt-2 text-base font-semibold leading-snug text-ink">{p.title}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <p className="text-sm font-medium text-ink/70">这些问题都可以直接问，我们会根据你的产品回答。</p>
              <ChatButton location="considerations" variant="primary" testId="problems-chat-button">
                WhatsApp 直接问我们
              </ChatButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* STAGE SELECTOR */}
      <section id="stages" className="relative scroll-mt-24 overflow-hidden bg-white py-14 md:py-20" data-testid="stage-section">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal>
            <SectionTag>你的阶段</SectionTag>
            <h2 className="max-w-2xl font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl lg:text-4xl">
              你目前在哪个阶段？
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-relaxed text-ink/60 md:text-base">
              点选最符合你的情况 —— 客服会直接接着你的阶段继续，不用重复说明。
            </p>
          </Reveal>
          <div className="mt-9 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {STAGES.map((s, i) => (
              <Reveal key={s.key} delay={i * 0.06}>
                <div className="flex h-full flex-col rounded-2xl border border-ink/10 bg-paper p-6 transition-colors duration-300 hover:border-forest/50" data-testid={`stage-card-${s.key}`}>
                  <span className="font-display text-lg italic text-moss">{s.num}</span>
                  <h3 className="mt-2.5 text-lg font-bold leading-snug text-ink">{s.title}</h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-ink/55">{s.desc}</p>
                  <button
                    onClick={() => openWhatsApp(`stage-${s.key}`, { text: s.cta })}
                    className="mt-5 w-full rounded-full border border-forest/40 py-2.5 text-sm font-semibold text-forest transition-colors hover:bg-forest hover:text-paper"
                    data-testid={`stage-cta-${s.key}`}
                    data-location={`stage-${s.key}`}
                  >
                    {s.cta}
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="home-services" className="scroll-mt-24 py-14 md:py-20" data-testid="services-section">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal className="md:flex md:items-end md:justify-between">
            <div>
              <SectionTag>后续支持</SectionTag>
              <h2 className="max-w-xl font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl lg:text-4xl">
                开始以后，我们还可以继续协助
              </h2>
              <p className="mt-3 max-w-lg text-sm text-ink/55 md:text-base">
                不同商家需要的支持不一样，可以根据实际情况选择。
              </p>
            </div>
            <Link to="/services" className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-forest md:mt-0" data-testid="services-view-all">
              查看完整服务内容
              <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <div className="mt-9 grid items-center gap-8 lg:grid-cols-[0.9fr_1.1fr]">
            <Reveal>
              <div className="overflow-hidden rounded-3xl border border-ink/10">
                <img
                  src="/img/content-operations-1024.webp"
                  srcSet="/img/content-operations-640.webp 640w, /img/content-operations-1024.webp 1024w, /img/content-operations-1600.webp 1600w"
                  sizes="(min-width: 1024px) 40vw, 100vw"
                  alt="TikTok Shop 内容制作与店铺运营支持"
                  loading="lazy"
                  className="aspect-[4/3] w-full object-cover"
                  data-testid="services-image"
                />
              </div>
            </Reveal>
            <div className="grid gap-3 sm:grid-cols-2">
              {HOME_SERVICES.map((s, i) => (
                <Reveal key={s.key} delay={(i % 4) * 0.06}>
                  <div className="flex h-full flex-col rounded-2xl border border-ink/10 bg-white p-6 transition-colors duration-300 hover:border-forest/40" data-testid={`service-card-${s.key}`}>
                    <span className="font-display text-sm italic text-moss">0{i + 1}</span>
                    <h3 className="mt-2.5 text-lg font-bold leading-snug text-ink">
                      <Link to={s.href} className="transition-colors hover:text-forest">{s.name}</Link>
                    </h3>
                    <p className="mt-1.5 flex-1 text-sm leading-relaxed text-ink/55">{s.desc}</p>
                    <div className="mt-5">
                      <ChatButton location={`service-${s.key}`} service={s.key} variant="sm" testId={`service-chat-${s.key}`}>
                        {s.cta}
                      </ChatButton>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE IMPROVE */}
      <section className="bg-white py-14 md:py-20" data-testid="improve-section">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal>
            <SectionTag>经营基础</SectionTag>
            <h2 className="max-w-2xl font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl lg:text-4xl">
              不只是把店开出来，而是把整个经营基础做好
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-9 rounded-3xl border border-ink/10 bg-paper p-5 shadow-[0_24px_70px_rgba(16,23,17,0.08)] md:p-7" data-testid="improve-dashboard">
              <div className="flex items-center justify-between border-b border-ink/10 pb-5">
                <div className="flex items-center gap-2.5">
                  <span className="h-3 w-3 rounded-full bg-clay/70" />
                  <span className="h-3 w-3 rounded-full bg-sand" />
                  <span className="h-3 w-3 rounded-full bg-lime" />
                </div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ink/40">你的店铺，认真经营</p>
              </div>
              <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {IMPROVE_AREAS.map((area, i) => (
                  <div key={area.name} className="rounded-xl border border-ink/8 bg-white p-5" data-testid={`improve-card-${i}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-bold text-ink">{area.name}</p>
                      <span className="h-1.5 w-1.5 rounded-full bg-lime" />
                    </div>
                    <p className="mt-1.5 text-xs leading-relaxed text-ink/50">{area.note}</p>
                    <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-sand">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${62 + ((i * 13) % 35)}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.15 + i * 0.06, ease: [0.22, 1, 0.36, 1] }}
                        className="h-full rounded-full bg-forest"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY TKSHOPMY */}
      <section className="bg-white py-14 md:py-20" data-testid="why-section">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal>
            <SectionTag>为什么找我们</SectionTag>
            <h2 className="max-w-2xl font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl lg:text-4xl">
              为什么商家会找 TKShopMY？
            </h2>
          </Reveal>
          <div className="mt-9 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_POINTS.map((w, i) => (
              <Reveal key={w.title} delay={(i % 4) * 0.06}>
                <div className="h-full rounded-2xl border border-ink/10 bg-paper p-6" data-testid={`why-point-${i}`}>
                  <h3 className="text-base font-bold text-ink">{w.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink/60">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-6 text-center font-display text-xl font-medium italic text-forest md:text-2xl" data-testid="why-highlight">
              先把情况讲清楚，再决定下一步。
            </p>
          </Reveal>
        </div>
      </section>

      {/* HOW TO START */}
      <section id="how-to-start" className="relative scroll-mt-24 overflow-hidden py-14 md:py-20" data-testid="process-section">
        <div className="mx-auto max-w-6xl px-5 md:px-8">
          <Reveal>
            <SectionTag>怎样开始</SectionTag>
            <h2 className="max-w-2xl font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl lg:text-4xl">
              从产品到开店，可以这样开始
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.07}>
                <div data-testid={`process-step-${i}`}>
                  <p className="font-display text-4xl font-light italic text-moss/70">{s.num}</p>
                  <div className="mt-3 h-px w-full bg-ink/10" />
                  <h3 className="mt-4 text-base font-bold text-ink">{s.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-ink/55">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-10">
              <ChatButton location="process" variant="primary" testId="process-chat-button">
                WhatsApp 问问我应该从哪里开始
              </ChatButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TRUST NOTE */}
      <section className="bg-sand py-10 md:py-12" data-testid="transparency-section">
        <div className="mx-auto max-w-3xl px-5 text-center md:px-8">
          <Reveal>
            <CoopNote
              variant="full"
              divider
              title="想轻一点开始？也可以了解一件代发模式。"
              body="TKShopMY 提供不同的 TikTok Shop 合作与运营方式，包括一件代发及无需自行囤货的模式。你可以先告诉我们你的产品、销售经验和目前情况，我们再说明比较适合怎样开始。"
              cta="了解合作方式"
              location="home-coop"
              testId="home-coop-note"
            />
          </Reveal>
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-moss">透明服务</p>
            <p className="mt-4 text-sm leading-relaxed text-ink/75 md:text-base">
              TKShopMY 会根据商家的实际产品、店铺和经营情况提供建议，不使用夸大的销售或收入承诺。
              TikTok Shop 的平台审核、流量和相关政策由平台最终决定。
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="py-14 md:py-20" data-testid="faq-preview-section">
        <div className="mx-auto max-w-4xl px-5 md:px-8">
          <Reveal>
            <SectionTag>常见问题</SectionTag>
            <h2 className="font-sans text-2xl font-bold leading-snug tracking-tight text-balance sm:text-3xl lg:text-4xl">
              商家常问的问题
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="mt-10">
            <FaqAccordion faqs={HOME_FAQS} testIdPrefix="home-faq" />
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 flex flex-wrap items-center gap-5">
              <Link to="/faq" className="group inline-flex items-center gap-2 text-sm font-semibold text-forest" data-testid="faq-view-all">
                查看全部常见问题
                <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <span className="text-sm text-ink/45">或</span>
              <ChatButton location="faq-preview" variant="sm" testId="faq-preview-chat-button">
                WhatsApp 直接问我们
              </ChatButton>
            </div>
          </Reveal>
        </div>
      </section>

      <FinalCta />
    </>
  );
}
